/**
 * The Fact Forge seed tree — the hand-authored core of the game, shared by the
 * client (instant offline lookups) and the server (which seeds it into
 * `ForgeElement`/`ForgeRecipe` as `isSeed = true`).
 *
 * This is the *seed*, not the whole game. Beyond these recipes the server
 * resolves novel pairs through a model once each and stores the answer forever,
 * so the live tree grows past what is written here. Keeping the seed in code
 * matters for two reasons: the opening moves stay deterministic and on-brand
 * (`Fact + Fact = Truth`, not whatever a model invents), and the client can
 * resolve them with no round trip at all.
 *
 * Recipes are order-independent: `Fact + Source` and `Source + Fact` hit the
 * same entry because the lookup key is alphabetically sorted.
 */
export const FORGE_BASE = ['Fact', 'Fiction', 'Source', 'Human'] as const;

export type Recipe = [string, string, string];

/** Exported so forgeRecipes.test.ts can assert the tree stays playable. */
export const RECIPES: Recipe[] = [
  // Tier 1 — the four starters against each other.
  ['Fact', 'Fiction', 'Debate'],
  ['Fact', 'Source', 'Citation'],
  ['Fact', 'Human', 'Belief'],
  ['Fiction', 'Source', 'Rumor'],
  ['Fiction', 'Human', 'Story'],
  ['Source', 'Human', 'Journalist'],
  ['Fact', 'Fact', 'Truth'],
  ['Fiction', 'Fiction', 'Myth'],
  ['Source', 'Source', 'Archive'],
  ['Human', 'Human', 'Crowd'],

  // Tier 2 — society shows up.
  ['Debate', 'Crowd', 'Election'],
  ['Citation', 'Archive', 'Encyclopedia'],
  ['Belief', 'Crowd', 'Religion'],
  ['Rumor', 'Crowd', 'Panic'],
  // Not Story + Crowd — that key already belongs to Meme below, and a repeated
  // key silently orphans whichever recipe loses. See the collision test.
  ['Story', 'History', 'Culture'],
  ['Journalist', 'Archive', 'Newspaper'],
  ['Truth', 'Myth', 'History'],
  ['Truth', 'Human', 'Honesty'],
  ['Myth', 'Human', 'Legend'],
  ['Crowd', 'Story', 'Meme'],

  // Tier 3 — the misinformation arc.
  ['Meme', 'Panic', 'Viral Hoax'],
  ['Newspaper', 'Viral Hoax', 'Correction'],
  ['Correction', 'Truth', 'Fact Check'],
  ['Fact Check', 'Crowd', 'Factiii'],
  ['Election', 'Panic', 'Scandal'],
  ['Scandal', 'Newspaper', 'Headline'],
  ['Headline', 'Meme', 'Clickbait'],
  ['Clickbait', 'Fact Check', 'Debunk'],
  ['Debunk', 'Crowd', 'Consensus'],
  ['Consensus', 'Truth', 'Science'],

  // Tier 4 — knowledge.
  ['Science', 'Archive', 'Library'],
  ['Science', 'Human', 'Scientist'],
  ['Scientist', 'Debate', 'Peer Review'],
  ['Peer Review', 'Citation', 'Paper'],
  ['Paper', 'Library', 'Knowledge'],
  ['Knowledge', 'Human', 'Wisdom'],
  ['Wisdom', 'Crowd', 'Civilization'],
  ['Civilization', 'Viral Hoax', 'Chaos'],
  ['Chaos', 'Fact Check', 'Order'],
  ['Order', 'Knowledge', 'Canon'],

  // Tier 5 — the economy.
  ['Factiii', 'Knowledge', 'Tron'],
  ['Tron', 'Crowd', 'Economy'],
  ['Economy', 'Chaos', 'Bubble'],
  ['Bubble', 'Truth', 'Crash'],
  ['Honesty', 'Crowd', 'Trust'],
  ['Trust', 'Economy', 'Currency'],
  ['Currency', 'Tron', 'Treasury'],
  ['Treasury', 'Chaos', 'Bailout'],

  // Tier 6 — the long view.
  ['Legend', 'History', 'Mythology'],
  ['Mythology', 'Science', 'Astronomy'],
  ['Astronomy', 'Human', 'Astronaut'],
  ['Astronaut', 'Chaos', 'Space Debris'],
  ['Religion', 'Science', 'Philosophy'],
  ['Philosophy', 'Human', 'Ethics'],
  ['Ethics', 'Debate', 'Law'],
  ['Law', 'Crowd', 'Government'],
  ['Government', 'Scandal', 'Resignation'],
  ['Culture', 'Knowledge', 'Education'],
  ['Education', 'Crowd', 'School'],
  ['Encyclopedia', 'Crowd', 'Open Archive'],

  // Tier 7 — the machines.
  ['Meme', 'Science', 'Data'],
  ['Data', 'Data', 'Big Data'],
  ['Big Data', 'Human', 'Algorithm'],
  ['Algorithm', 'Meme', 'Feed'],
  ['Feed', 'Fact Check', 'Verified Feed'],
  ['Algorithm', 'Knowledge', 'AI'],
  ['AI', 'Fiction', 'Hallucination'],
  ['Hallucination', 'Fact Check', 'Alignment'],
  ['AI', 'Truth', 'Oracle'],
  ['Oracle', 'Crowd', 'Prophecy']
];

export const FORGE_EMOJI: Record<string, string> = {
  Fact: '📄',
  Fiction: '🎭',
  Source: '🔗',
  Human: '🧍',
  Debate: '🗣️',
  Citation: '📎',
  Belief: '🙏',
  Rumor: '🐍',
  Story: '📖',
  Journalist: '🕵️',
  Truth: '💎',
  Myth: '🐉',
  Archive: '🗄️',
  Crowd: '👥',
  Election: '🗳️',
  Encyclopedia: '📚',
  Religion: '⛪',
  Panic: '😱',
  Culture: '🎨',
  Newspaper: '📰',
  History: '🏛️',
  Honesty: '🤝',
  Legend: '🗿',
  Meme: '😂',
  'Viral Hoax': '☣️',
  Correction: '✏️',
  'Fact Check': '✅',
  Factiii: '🔵',
  Scandal: '💣',
  Headline: '📢',
  Clickbait: '🪤',
  Debunk: '🔨',
  Consensus: '🧭',
  Science: '🔬',
  Library: '📗',
  Scientist: '👩‍🔬',
  'Peer Review': '🧾',
  Paper: '📃',
  Knowledge: '🧠',
  Wisdom: '🦉',
  Civilization: '🏙️',
  Chaos: '🌪️',
  Order: '🧱',
  Canon: '📜',
  Tron: '⚡',
  Economy: '📈',
  Bubble: '🫧',
  Crash: '📉',
  Trust: '🕊️',
  Currency: '🪙',
  Treasury: '🏦',
  Bailout: '🧯',
  Mythology: '🏺',
  Astronomy: '🔭',
  Astronaut: '👨‍🚀',
  'Space Debris': '🛰️',
  Philosophy: '🤔',
  Ethics: '⚖️',
  Law: '📕',
  Government: '🏢',
  Resignation: '🚪',
  Education: '🎓',
  School: '🏫',
  'Open Archive': '🌐',
  Data: '📊',
  'Big Data': '🗃️',
  Algorithm: '🧮',
  Feed: '📱',
  'Verified Feed': '🛡️',
  AI: '🤖',
  Hallucination: '🌈',
  Alignment: '🧷',
  Oracle: '🔮',
  Prophecy: '📿'
};

/**
 * Lookup keys are order-independent, which means `[a, b]` and `[b, a]` collide.
 * Exported so the test can prove no two recipes share a key — when they do, the
 * Map silently keeps only the last one and orphans whole branches of the tree.
 */
export const keyFor = (a: string, b: string) => [a, b].sort().join(' + ');

const RECIPE_LOOKUP = new Map(
  RECIPES.map(([a, b, result]) => [keyFor(a, b), result])
);

export const combine = (a: string, b: string): string | null =>
  RECIPE_LOOKUP.get(keyFor(a, b)) ?? null;

/** Base elements plus everything a recipe can produce. */
export const TOTAL_ELEMENTS =
  FORGE_BASE.length + new Set(RECIPES.map(([, , result]) => result)).size;
